import React, { useCallback, useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import InviteForm from "../components/InviteForm";
import { Alert, Card } from "react-bootstrap";
import LoadingPlaceholder from "../components/LoadingPlaceholder";
import { UserPartial } from "../models/user.model";
import Banner from "../components/Banner";
import { USER_AUTH_TYPE } from "../enums/user-auth-type.enum";
import { useAppDispatch, useAppSelector } from "../store";
import {
  getEnrollment,
  getUserOwner,
} from "../store/user-owner/user-owner.thunk";
import {
  selectUserOwner,
  selectUserOwnerError,
  selectUserOwnerLoading,
  selectUserProcessing,
} from "../store/user-owner/user-owner.selector";
import LocationList from "../components/LocationList";
import { ProgramType } from "../models/program-type.model";
import { PROGRAM_TYPE_ENUM } from "../enums/program-type.enum";
import { APPLICATION_ENUM } from "../enums/application.enum";
import { useLocation } from "react-router-dom";
import UserProcessingModal from "../components/modals/UserProcessingModal";
import useTokenClaims from "../hooks/useTokenClaims";
import OwnerDataProcessinggModal from "../components/modals/OwnerDataProcessingModal";

export default function Dashboard() {
  const location = useLocation();
  const { accounts } = useMsal();
  const dispatch = useAppDispatch();
  const { inProgress } = useMsal();
  const [program, setProgram] = useState("");
  const [app, setApp] = useState("");
  const [claims, setClaims] = useState<UserPartial | null>(null);
  const owner = useAppSelector(selectUserOwner);
  const ownerLoading = useAppSelector(selectUserOwnerLoading);
  const userProcessing = useAppSelector(selectUserProcessing);
  const ownerError = useAppSelector(selectUserOwnerError) as string | undefined;
  const [ownerId, setOwnerId] = useState<string | null>(null);
  const [hasCompletedInvitation, setHasCompletedInvitation] = useState(false);
  const [hasCompletedRegistration, setHasCompletedRegistration] =
    useState(false);
  const [isProgramEnrolled, setIsProgramEnrolled] = useState(false);
  const [showUserProcessingModal, setShowUserProcessingModal] = useState(false);
  const [showOwnerDataProcessingModal, setShowOwnerDataProcessingModal] = useState(false);
  const { getTokenWithClaims } = useTokenClaims();
  useEffect(() => {
    if (owner && owner.employee) {
      let programType = "";
      const salesOrg = owner.employee
        .salesOrganization as keyof typeof PROGRAM_TYPE_ENUM;
      // console.log('- salesOrg => ', salesOrg);
      if (salesOrg) {
        programType = PROGRAM_TYPE_ENUM[salesOrg];
      } else if (owner.programType) {
        programType = owner.programType;
      }
      // console.log('- programType => ', programType);
      let isReadOnly: boolean = false; // = owner.programs.some(program => program.loyaltyID === `${programType}-${ownerId}`);
      let isEditible: boolean = false; // = owner.programs.some(program => program.loyaltyID === `${programType}-${ownerId}`);
      const foundProgram = owner.programs.find(
        (program) =>
          program.loyaltyID === `${programType}-${ownerId}` ||
          program.loyaltyID === `${programType}-${owner.ownerID}`
      );
      // console.log('- foundProgram => ', foundProgram);
      if (foundProgram) {
        // console.log('- foundProgram?.enrolledDate => ', foundProgram?.enrolledDate);
        setIsProgramEnrolled(foundProgram?.enrolled || false);
        if (foundProgram?.enrolledDate) {
          isReadOnly = true;
        } else {
          isEditible = true;
        }
        // console.log('- isReadOnly => ', isReadOnly);
        // console.log('- isEditible => ', isEditible);
      }

      setProgram(programType);
      setHasCompletedRegistration(isReadOnly);
      setHasCompletedInvitation(isEditible);
    }
  }, [owner, ownerId]);

  const handleGetClaims = useCallback(async () => {
    try {
      if (location.pathname === '/invite') {
        const tokenData = await getTokenWithClaims();
        const claims = tokenData?.idTokenClaims as UserPartial;
        setClaims(claims);
        setApp(APPLICATION_ENUM.INVITE_PORTAL);
      } else if (accounts.length > 0){
        const claims = accounts[0].idTokenClaims as UserPartial;
        setClaims(claims);
        setApp(APPLICATION_ENUM.REGISTRATION_PORTAL);
      }
    } catch (error) {
      console.error("Failed to get token claims:", error);
    }
  }, [accounts, getTokenWithClaims, location.pathname]);

  useEffect(() => {
    if (!claims) {
      handleGetClaims();
    }
  }, [claims, handleGetClaims]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    if (app === APPLICATION_ENUM.INVITE_PORTAL) {
      const ownerId = query.get("ownerId") || localStorage.getItem("ownerId");
      const employeeId =  query.get("employeeId") ||  localStorage.getItem("employeeId");
      setOwnerId(ownerId);
      if (ownerId && employeeId) {
        dispatch(getUserOwner({ ownerId, employeeId }));
      }
    } else if (app === APPLICATION_ENUM.REGISTRATION_PORTAL) {
      const token = query.get("token") || localStorage.getItem("registrationToken");
      if (token) {
        dispatch(getEnrollment({ token }));
      }
    }
  }, [dispatch, app, location.search]);

  useEffect(() => {
    if (typeof userProcessing !== "undefined") {
      setShowUserProcessingModal(!!userProcessing);
    }
  }, [userProcessing]);

  useEffect(() => {
    if (claims) {
      setShowOwnerDataProcessingModal(!!ownerLoading);
    }
  }, [claims, ownerLoading]);
  const supportEmail = program === PROGRAM_TYPE_ENUM.PLG_SALES_ORG
    ? 'PRsupport@essilorusa.com'
    : 'PBsupport@essilorusa.com';
  return (
    <>
      {inProgress === "handleRedirect" && <LoadingPlaceholder />}
      {claims && (
        <div className="container pt-4">
          {ownerLoading ? (
            <LoadingPlaceholder />
          ) : (
            <>
              {ownerError ? (
                <div className="bg-white rounded-sm p-4 shadow-sm flex flex-col gap-1 mt-4">
                  <div className="flex flex-col">
                    <Alert className="text-center mx-auto" variant="danger">
                      {ownerError}
                    </Alert>
                  </div>
                </div>
              ) : (
                <>
                  <Banner
                    userAuthType={claims.acr as USER_AUTH_TYPE}
                    app={app}
                    program={`${
                      program === PROGRAM_TYPE_ENUM.PLG_SALES_ORG
                        ? 'Partner Rewards Hub'
                        : 'PracticeBuilder'
                    }`}
                    title={`${
                      program === PROGRAM_TYPE_ENUM.PLG_SALES_ORG
                        ? 'Partner Rewards Hub'
                        : 'PracticeBuilder'
                    } - ${app}`}
                    support={`${
                      program === PROGRAM_TYPE_ENUM.PLG_SALES_ORG
                        ? 'PRsupport@essilorusa.com'
                        : 'PBsupport@essilorusa.com'
                    }`}
                  />
                  <div className="bg-white rounded-sm p-4 shadow-sm flex flex-col gap-1 mt-4">
                    <h3 className="font-semibold text-2xl text-center">
                      {' '}
                      {claims.acr === USER_AUTH_TYPE.COMARCH_EMPLOYEE
                        ? 'Invitation'
                        : 'Registration'}
                    </h3>
                    {!owner || owner?.labLocations.length === 0 ? (
                      <div className="bg-white rounded-sm p-4 shadow-sm flex flex-col gap-1 mt-4">
                        <div className="flex flex-col">
                          <Alert
                            className="text-center mx-auto"
                            variant="danger"
                          >
                            We had a problem trying to retrieve the information
                            you're looking for. Please reach out to customer
                            service for assistance.
                          </Alert>
                        </div>
                      </div>
                    ) : (
                      <>
                        {app === 'Invite Portal' && (
                          <>
                            {/* Header */}
                            <div className="flex items-center gap-2">
                              <span className="text-blue-600 text-lg">ℹ️</span>
                              <h3 className="text-lg font-semibold text-blue-900">
                                Review Practice Information
                              </h3>
                            </div>
                            <div className="space-y-3">
                              <p className="text-gray-700">Please review the listed practice, location(s) and lab account(s)</p>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                              <div className="flex items-start gap-2">
                                <span className="text-amber-600 text-sm">✉️</span>
                                <p className="text-sm text-amber-800">
                                  <strong>Need updates?</strong> Contact{' '}
                                  <a
                                    href={`mailto:${supportEmail}`}
                                    className="text-amber-900 underline hover:text-amber-700"
                                  >
                                    {supportEmail}
                                  </a>
                                  {' '}before sending the invite.
                                </p>
                              </div>
                            </div>
                          </>
                        )}
                        {app === 'Registration Portal' && (
                          <>
                            {/* Header */}
                            <div className="flex items-center gap-2">
                              <span className="text-blue-600 text-lg">ℹ️</span>
                              <h3 className="text-lg font-semibold text-blue-900">
                                Review Practice Information
                              </h3>
                            </div>
                            <div className="space-y-3">
                              <p className="text-gray-700">Please review the listed practice location(s), lab account(s), and personal
                                information.</p>
                            </div>
                            <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                              <div className="flex items-start gap-2">
                                <span className="text-amber-600 text-sm">✉️</span>
                                <p className="text-sm text-amber-800">
                                  <strong>Need updates?</strong> Contact{' '}
                                  <a
                                    href={`mailto:${supportEmail}`}
                                    className="text-amber-900 underline hover:text-amber-700"
                                  >
                                    {supportEmail}
                                  </a>
                                  {' '}before accepting the Terms & Conditions.
                                </p>
                              </div>
                            </div>
                          </>
                        )}
                      </>
                    )}
                  </div>
                  <div className="bg-white rounded-sm p-4 shadow-sm flex flex-col gap-1 mt-4">
                    <div className="flex flex-col">
                      {app === 'Invite Portal' && (
                        <>
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                            <div className="flex items-start gap-2">
                              <span className="text-amber-600 text-sm">ⓘ</span>
                              <p className="text-sm text-amber-800">
                                <strong>Note:</strong> “In the instance information below is hard to read, you can resize the column for an easier viewing
                                experience.
                                For any further details on how ECP can find account information click the (?) icon.
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                      {app === 'Registration Portal' && (
                        <>
                          <div className="bg-amber-50 border border-amber-200 rounded-md p-3">
                            <div className="flex items-start gap-2">
                              <span className="text-amber-600 text-sm">ⓘ</span>
                              <p className="text-sm text-amber-800">
                                <strong>Note:</strong> “In the instance information below is hard to read, you can resize the column for an easier viewing
                                experience.
                                For any further details on how ECP can find account information click the (?) icon.
                              </p>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                    {/* Practice Information */}
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-blue-600 text-lg">🏢</span>
                      <h4 className="font-medium text-gray-900">Practice Name</h4>
                    </div>
                    <h1 className="ml-6">
                      {owner?.ownerName?.replace('Owner - Geo - ', '').replace('Owner - ', '') || 'Not specified'}
                    </h1>
                    <LocationList/>
                  </div>
                  <Card className="mt-4">
                    <Card.Header>
                      {app === 'Invite Portal' && (
                        <>
                        <h4 className="font-bold">Invite Form</h4>
                        </>
                      )}
                      {app === 'Registration Portal' && (
                        <>
                          <h4 className="font-bold">Registration Form</h4>
                        </>
                      )}
                    </Card.Header>
                    <Card.Body>
                      <InviteForm
                        hasCompletedInvitation={hasCompletedInvitation}
                        hasCompletedRegistration={hasCompletedRegistration}
                        isProgramEnrolled={isProgramEnrolled}
                        owner={owner}
                        programType={program as ProgramType}
                        userAuthType={claims.acr as USER_AUTH_TYPE}
                      />
                    </Card.Body>
                  </Card>
                </>
              )}
            </>
          )}
        </div>
      )}
      {/*<UserProcessingModal*/}
      {/*  show={showUserProcessingModal}*/}
      {/*  handleClose={() => setShowUserProcessingModal(false)}*/}
      {/*/>*/}
      <OwnerDataProcessinggModal
        show={showOwnerDataProcessingModal}
        handleClose={() => setShowOwnerDataProcessingModal(false)}
      />
    </>
  );
}
